import { hasDeliveryRevision } from './checkoutDeliveryAuthority';
import { completedCheckoutOrder } from './checkoutCompletion';
import getMissingEmbeddedCheckoutFields from './embeddedCheckoutContract';

export const validExistingRevision = (value) =>
  value === null || hasDeliveryRevision(value);

export const canRetrieveExistingPayment = (resolution, checkoutId) =>
  resolution?.checkout_id === checkoutId &&
  Object.prototype.hasOwnProperty.call(resolution, 'delivery_revision') &&
  validExistingRevision(resolution.delivery_revision) &&
  resolution.existing_payment_action === 'retrieve_payment_intent' &&
  ['unpaid', 'action_required'].includes(resolution.state) &&
  !resolution.operation_pending &&
  !resolution.already_confirmed;

// A credential read is not durable payment permission. Every submission must
// observe the latest exact-checkout resolution, independent of replay flags.
export const canSubmitExistingPayment = (result, checkout) =>
  !['pending', 'error'].includes(result?.status) &&
  !checkout?.isEnded &&
  !!checkout?.id &&
  result?.resolution?.checkout_id === checkout.id &&
  Object.prototype.hasOwnProperty.call(
    result.resolution,
    'delivery_revision'
  ) &&
  validExistingRevision(result.resolution.delivery_revision) &&
  result.resolution.delivery_revision === checkout.delivery_revision &&
  ['unpaid', 'action_required'].includes(result.resolution.state) &&
  !result.resolution.operation_pending &&
  !result.resolution.already_confirmed;

export const EXISTING_AMOUNT_EVIDENCE_REQUIRED =
  'The original tip-inclusive payable amount is unavailable. The retrieval contract supplies issued cents but no independent payable basis. Reconciliation is required before resuming.';

export const existingPaymentReadRetryAllowed = (error) => {
  const code = error?.data?.data?.code || error?.data?.code;
  return (
    code === 'checkout_evidence_unavailable' ||
    (!error?.status && !code) ||
    error?.status >= 500
  );
};

// Only fixed messages/codes leave this boundary. Never propagate payloads,
// provider messages, Axios errors/configs or schema diagnostics.
export const existingPaymentFailure = (error) => {
  const code = error?.data?.data?.code || error?.data?.code;
  const messages = {
    existing_payment_evidence_unproven:
      'Original payment evidence is unproven. Reconciliation is required.',
    existing_payment_not_resumable:
      'This existing payment cannot resume. Check its status; do not pay again.',
    checkout_recovery_ambiguous:
      'More than one payment history requires reconciliation. No payment was selected.',
    checkout_operation_pending:
      'A checkout operation is pending. Check this checkout status again.',
    checkout_resolution_changed:
      'Checkout status changed during retrieval. Check this checkout status again.',
    checkout_evidence_unavailable:
      'Payment evidence is temporarily unavailable. Retry this existing-payment read or check status.',
    stale_checkout:
      'The retained checkout is unavailable. Reconciliation is required; no replacement was started.',
    invalid_checkout_request:
      'The existing-payment retrieval request was rejected. Contract correction is required.',
  };
  if ([401, 403].includes(error?.status))
    return 'Authentication or checkout ownership could not be verified. Sign in to the original account and check status.';
  if (error?.status === 400) return messages.invalid_checkout_request;
  if (error?.status === 404) return messages.stale_checkout;
  return (
    messages[code] ||
    'Existing payment retrieval failed. Retry this read or check status; no new payment was started.'
  );
};

export function validateExistingPayment(response, checkout, revision) {
  if (
    response?.checkout_id !== checkout.id ||
    !Object.prototype.hasOwnProperty.call(response, 'delivery_revision') ||
    response.delivery_revision !== revision ||
    !validExistingRevision(revision)
  )
    throw new Error('Existing payment identity or revision does not match.');
  const order = completedCheckoutOrder(response, checkout.id);
  if (order) return { order };
  const credentials = {
    clientSecret: response.client_secret,
    publishableKey: response.publishable_key,
    paymentIntentId: response.payment_intent_id,
    stripeAccountId: response.stripe_account_id,
    amount: response.amount,
  };
  if (
    response.success !== true ||
    !['unpaid', 'action_required'].includes(response.state) ||
    response.operation_pending ||
    response.already_confirmed ||
    response.replacement_allowed !== false ||
    response.currency !== 'usd' ||
    getMissingEmbeddedCheckoutFields(credentials).length ||
    typeof credentials.stripeAccountId !== 'string' ||
    !credentials.stripeAccountId.trim() ||
    (checkout.paymentIntentId &&
      checkout.paymentIntentId !== credentials.paymentIntentId) ||
    (checkout.stripeAccountId &&
      checkout.stripeAccountId !== credentials.stripeAccountId)
  )
    throw new Error('Existing payment credentials or state do not match.');
  // This receipt is the original issued payable basis, including any legacy
  // issuance tip. Current quote.total is not a zero-tip reconstruction oracle.
  if (
    !Number.isSafeInteger(checkout.issuedAmount) ||
    checkout.issuedAmount <= 0
  )
    throw new Error(EXISTING_AMOUNT_EVIDENCE_REQUIRED);
  if (checkout.issuedAmount !== credentials.amount)
    throw new Error('Existing issued payment amount changed.');
  return { credentials };
}
